// ABOUTME: The header search launcher's results dropdown: a listbox of links, actions and web searches.
// ABOUTME: Presentational only; selection and opening are owned by useLauncher.

import React, { useEffect, useRef } from "react";
import { domainOf, linkLabel } from "../utils/linkLabel";
import type { LauncherRow } from "../hooks/useLauncher";

interface LauncherResultsProps {
  id: string;
  rows: LauncherRow[];
  active: number;
  onHover: (index: number) => void;
  onChoose: (row: LauncherRow, forceNewTab: boolean) => void;
}

/** The DOM id of a row, for the field's aria-activedescendant. */
export function launcherOptionId(listboxId: string, index: number): string {
  return `${listboxId}-option-${index}`;
}

function rowKey(row: LauncherRow): string {
  if (row.kind === "item") return `item-${row.item.id}`;
  if (row.kind === "search") return `search-${row.engine}`;
  return `action-${row.id}`;
}

const searchIcon = (
  <svg
    aria-hidden="true"
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

const LauncherResults: React.FC<LauncherResultsProps> = ({
  id,
  rows,
  active,
  onHover,
  onChoose,
}) => {
  const listRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    listRef.current
      ?.querySelector('[aria-selected="true"]')
      ?.scrollIntoView?.({ block: "nearest" });
  }, [active]);

  return (
    <div
      className="absolute left-0 right-0 top-full mt-1 z-50 overflow-hidden rounded-[var(--radius-md)]
                 border border-[var(--color-border)] bg-[var(--color-bg-secondary)] shadow-lg"
      // Keeps focus in the field, so a click on a row lands before the field's
      // blur closes the dropdown.
      onMouseDown={(e) => e.preventDefault()}
    >
      <ul
        ref={listRef}
        id={id}
        role="listbox"
        aria-label="Search results"
        className="max-h-80 overflow-y-auto py-1"
      >
        {rows.map((row, idx) => {
          const isActive = idx === active;
          return (
            <li
              key={rowKey(row)}
              id={launcherOptionId(id, idx)}
              role="option"
              aria-selected={isActive}
              onMouseEnter={() => onHover(idx)}
              onClick={(e) => onChoose(row, e.metaKey || e.ctrlKey)}
              className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors
                          ${isActive ? "bg-[var(--color-bg-hover)]" : ""}`}
            >
              {row.kind === "item" ? (
                <>
                  <span className="w-5 h-5 shrink-0 flex items-center justify-center text-xs font-semibold uppercase rounded bg-[var(--color-bg)] text-[var(--color-text-tertiary)]">
                    {(row.item.type === "widget"
                      ? row.item.widgetName || "?"
                      : linkLabel(row.item) || "?"
                    ).charAt(0)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-[var(--color-text-primary)]">
                      {row.item.type === "widget"
                        ? row.item.name || row.item.widgetName
                        : linkLabel(row.item)}
                    </span>
                    {row.item.url && (
                      <span className="block truncate text-xs text-[var(--color-text-tertiary)]">
                        {domainOf(row.item.url) || row.item.url}
                      </span>
                    )}
                  </span>
                  {row.item.type === "widget" && (
                    <span className="text-xs text-[var(--color-text-muted)] shrink-0">widget</span>
                  )}
                </>
              ) : row.kind === "action" ? (
                <>
                  <span className="w-5 h-5 shrink-0 flex items-center justify-center rounded-full bg-[var(--color-accent)] text-[var(--color-text-inverse)]">
                    <svg
                      aria-hidden="true"
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    >
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm text-[var(--color-accent)]">
                    {row.label}
                  </span>
                </>
              ) : (
                <>
                  <span className="w-5 h-5 shrink-0 flex items-center justify-center text-[var(--color-text-tertiary)]">
                    {searchIcon}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm text-[var(--color-text-primary)]">
                    Search {row.engine} for “{row.query}”
                  </span>
                </>
              )}
            </li>
          );
        })}
      </ul>
      <div className="hidden sm:flex items-center gap-4 px-3 py-1.5 border-t border-[var(--color-border)] text-xs text-[var(--color-text-muted)]">
        <span>↑↓ navigate</span>
        <span>↵ open</span>
        <span>⌘↵ new tab</span>
        <span>esc clear</span>
      </div>
    </div>
  );
};

export default LauncherResults;
